import { action, makeObservable, observable } from 'mobx';
import {
  BetStatus,
  notificationStore,
  parseNumberByPrecision,
  popupStore,
} from '@pascal/pascal-core';
import { applicationStore } from '@pascal/pascal-core/shared/core/store';
import { freeBetStore } from '@pascal/pascal-core/shared/core/store/freeBetStore';
import { gameStore } from './gameStore';
import { betDataStore, oddStore } from './index';
import { gameLiveConnection, soundService } from '../services';

class DoBetStore {
  doBetSectionsInitialData = {
    betId: null,
    isBetAccepted: false,
    winAmount: null,
    isDisabled: false,
    currentAutoCashOutOdd: null,
    isBetPending: false,
    freeBetId: null,
    isFreeBetAccepted: false,
    freeBetType: null,
    freeBetCurrentOdd: null,
    freeBetCurrentAmount: null,
  };

  doBetSectionsData = {};

  cashOutDisabled = {};

  freeBetMinOdd = null;

  winData = {};

  constructor() {
    makeObservable(this, {
      freeBetMinOdd: observable,
      cashOutDisabled: observable.deep,
      winData: observable.deep,
      doBetSectionsData: observable.deep,
      setInitialDefaultDoBetData: action.bound,
      setBetAccepted: action.bound,
      sendCashOut: action.bound,
      setDoBetResult: action.bound,
      doBetSender: action.bound,
      setCashOutDisabled: action.bound,
      setIsBetPendingState: action.bound,
      setIsDisabled: action.bound,
      setCurrentAutoCashOutValue: action.bound,
      handleWinData: action.bound,
      setWinData: action.bound,
      resetDoBetData: action.bound,
      setCurrentFreeBetId: action.bound,
      setFreeBetMinOdd: action,
    });
  }

  setInitialDefaultDoBetData(gameTypeId, currentBetData) {
    if (currentBetData) {
      this.doBetSectionsData[gameTypeId] = {
        betId: currentBetData.id,
        isBetAccepted: currentBetData.status === BetStatus.Pending,
        isDisabled: !!currentBetData.minOdd,
        currentAutoCashOutOdd: currentBetData.betData || null,
        isBetPending: false,
        freeBetId: currentBetData?.freeBetId || null,
        isFreeBetAccepted: false,
        freeBetType: null,
        freeBetCurrentAmount: null,
        freeBetCurrentOdd: currentBetData.betData || null,
      };
      this.winData[gameTypeId] = false;
    } else {
      this.doBetSectionsData[gameTypeId] = {
        betId: null,
        isBetAccepted: false,
        isDisabled: false,
        currentAutoCashOutOdd: null,
        isBetPending: false,
        freeBetId: null,
        isFreeBetAccepted: false,
        freeBetType: null,
        freeBetCurrentAmount: null,
        freeBetCurrentOdd: null,
      };
      this.winData[gameTypeId] = false;
    }
  }

  setFreeBetMinOdd(odd) {
    this.freeBetMinOdd = odd;
  }

  setFreeBetType(type, gameTypeId) {
    this.doBetSectionsData[gameTypeId].freeBetType = type;
  }

  setFreeBeetDefaultOdd(freeBetId, currentUserFreebet) {
    const freeBetMinOdd =
      currentUserFreebet?.minOdd ||
      freeBetStore.activeFreeBet.minOdd ||
      freeBetStore.freeBets.find((item) => item.id === freeBetId)?.minOdd;
    this.setFreeBetMinOdd(freeBetMinOdd);
  }

  setCurrentFreeBetId(id, gameTypeId) {
    this.doBetSectionsData[gameTypeId].freeBetId = id;
  }

  setCurrentFreeBetAmount(freeBetId, gameTypeId) {
    this.doBetSectionsData[gameTypeId].freeBetCurrentAmount =
      freeBetStore.activeFreeBet.amount ||
      freeBetStore.freeBets.find((item) => item.id === freeBetId).amount;
  }

  setFreeBetCurrentOdd(odd, gameTypeId) {
    this.doBetSectionsData[gameTypeId].freeBetCurrentOdd = odd;
  }

  setIsBetPendingState(bool, gameTypeId) {
    this.doBetSectionsData[gameTypeId].isBetPending = bool;
  }

  setCurrentAutoCashOutValue(value, gameTypeId) {
    this.doBetSectionsData[gameTypeId].currentAutoCashOutOdd = value;
  }

  setCashOutDisabled(bool, gameTypeId) {
    if (gameTypeId) {
      this.cashOutDisabled[gameTypeId] = bool;
    }
  }

  clearCashOutDisabled() {
    this.cashOutDisabled = {};
  }

  setIsDisabled(bool, gameTypeId) {
    if (gameTypeId) {
      this.doBetSectionsData[gameTypeId].isDisabled = bool;
    }
  }

  setBetAccepted(betId, boolean, gameTypeId) {
    this.doBetSectionsData[gameTypeId].isBetAccepted = boolean;
    this.doBetSectionsData[gameTypeId].betId = betId;
    if (boolean) {
      soundService.playBetAccepted();
    }
  }

  // increaseDemoBalance(winAmount) {
  //   const balance = +(userInfoStore.balance + winAmount).toFixed(2);
  //   userInfoStore.setDemoBalance(balance);
  // }

  // decreaseDemoBalance(betAmount) {
  //   const balance = +(userInfoStore.balance - betAmount).toFixed(2);
  //   userInfoStore.setDemoBalance(balance);
  // }

  handleClearRoundFin(bets, gameTypeId) {
    if (this.doBetSectionsData[gameTypeId]?.isBetAccepted) {
      this.setBetAccepted(null, false, gameTypeId);
    }
    if (this.doBetSectionsData[gameTypeId].currentAutoCashOutOdd) {
      this.setCurrentAutoCashOutValue(null, gameTypeId);
    }
    this.setCurrentFreeBetId(null, gameTypeId);
  }

  // setDemoBetData(data, gameTypeId) {
  //   this.doBetSectionsData[gameTypeId] = { ...this.doBetSectionsData[gameTypeId], ...data };
  // }

  doBetSender(gameTypeId) {
    const roundId = gameStore.roundId[gameTypeId];
    const { autoCashOutOdd, isAutoCashOut, betAmount } = betDataStore.getBetData(gameTypeId);

    // if (isDemo) {
    // const demoDoBetData = {
    //   amount: betAmount,
    //   roundId,
    //   gameTypeId,
    //   betData: isAutoCashOut ? autoCashOutOdd : 0,
    //   status: BetStatus.Pending,
    //   id: createGuid(),
    //   responseCode: BetResponseCode.Success,
    //   date: new Date(),
    //   winAmount: 0,
    // };

    // if (betDataStore.betsSectionsData[gameTypeId].isAutoBet) {
    //   betDataStore.decreaseAutoBetCount(gameTypeId);
    // }
    // oddStore.setCurrentBetAmount(betAmount, gameTypeId);
    // this.decreaseDemoBalance(betAmount);
    // this.setDemoBetData(demoDoBetData, gameTypeId);
    // betStore.updateLiveBets(demoDoBetData);
    // this.setDoBetResult(demoDoBetData);
    // }
    const { activeFreeBet } = freeBetStore;
    this.setIsBetPendingState(true, gameTypeId);
    const doBetModel = {
      amount: activeFreeBet.id ? activeFreeBet.amount : betAmount,
      roundId,
      gameTypeId: gameTypeId,
      freeBetId: activeFreeBet.id,
      betData: isAutoCashOut ? autoCashOutOdd : 0,
    };
    gameLiveConnection.doBet(doBetModel);
  }

  setDoBetResult({ id, responseCode, gameTypeId, betData }) {
    const { isDemo } = applicationStore;
    if (isDemo) {
      popupStore.updateDemoBetsCount();
    }
    this.setBetAccepted(id, true, gameTypeId);
    this.setCashOutDisabled(true, gameTypeId);
    this.setCurrentAutoCashOutValue(betData, gameTypeId);
    notificationStore.setNotificationType({ responseCode: responseCode });
  }

  sendCashOut(gameTypeId) {
    const betId = this.doBetSectionsData[gameTypeId].betId;
    gameLiveConnection.cashOut({ betId, roundId: gameStore.roundId[gameTypeId], gameTypeId });
    // this.setBetAccepted(betId, false, gameTypeId);
  }

  setWinData(winAmount, gameTypeId) {
    this.winData[gameTypeId] = winAmount;
  }

  handleWinData({ winAmount, gameTypeId }) {
    const parsWinAmount = parseNumberByPrecision(1, winAmount, 2);
    this.setCurrentFreeBetId(null, gameTypeId);

    this.setWinData(parsWinAmount, gameTypeId);

    if (this.doBetSectionsData[gameTypeId].currentAutoCashOutOdd) {
      this.setCurrentAutoCashOutValue(null, gameTypeId);
    }

    soundService.playWin();
    oddStore.setFinishWinAmount(winAmount, gameTypeId);

    setTimeout(() => {
      this.setWinData(null, gameTypeId);
    }, 2000);

    // if (isMaxWinByGameType) {
    //   setIsMaxWinByGameType(false);
    // }
    // todo
    // if (isFreeBetMaxWin) {
    //   setFreeBetMaxWin(false);
    // }
    // if (!isDemo) {
    setTimeout(() => gameLiveConnection.getBalance(), 500);
    // } else {
    //   this.increaseDemoBalance(winAmount);
    // }
  }

  resetDoBetData(allGameTypes) {
    allGameTypes.forEach((gameTypeId) => {
      this.doBetSectionsData[gameTypeId] = this.doBetSectionsInitialData;
      this.setWinData(null, gameTypeId);
    });
    this.clearCashOutDisabled();
  }
}

export const doBetStore = new DoBetStore();
